import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

// Step 1: Define the Entity Adapter and Initial State
const notificationsAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.createdAt.localeCompare(a.createdAt),
  selectId: (notification) => notification._id,
});

const initialState = notificationsAdapter.getInitialState();

// Step 2: Create the API Slice with Endpoints
export const notificationApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserNotifications: builder.query({
      query: () => '/notifications',
      transformResponse: (responseData) => {
        return notificationsAdapter.setAll(initialState, responseData);
      },
      providesTags: (result, error, arg) => 
        result ? [...result.ids.map((id) => ({ type: 'Notification', id }))] : [{ type: 'Notification', id: 'LIST' }],
    }),
    updateNotification: builder.mutation({
      query: (notificationId) => ({
        url: `/notifications`,
        method: 'PATCH',
        body: { id: notificationId },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Notification', id: arg.id }, { type: 'Offers', id: 'LIST' }],
    }),
    markAllNotificationsAsRead: builder.mutation({
      query: () => ({
        url: `/notifications/markAllAsRead`,
        method: 'PATCH',
      }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
  }),
});

// Step 3: Export Hooks and Selectors
export const {
  useGetUserNotificationsQuery,
  useUpdateNotificationMutation,
  useMarkAllNotificationsAsReadMutation,
} = notificationApiSlice;

export const selectNotificationsResult = notificationApiSlice.endpoints.getUserNotifications.select();

const selectNotificationsData = createSelector(
  selectNotificationsResult,
  (notificationsResult) => notificationsResult.data
);

export const {
  selectAll: selectAllNotifications,
  selectById: selectNotificationById,
} = notificationsAdapter.getSelectors(
  (state) => selectNotificationsData(state) ?? initialState
);
